import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle,
  Circle,
  Users,
  Building,
  ClipboardCheck,
  FileText,
  Shield,
  Plug,
  X,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Layers,
  Clock,
} from "lucide-react";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { hasAnyRole } from "@/lib/rbac";

interface SetupTask {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  isCompleted: boolean;
  isOptional?: boolean;
  requiredRoles?: string[]; // Only show task to users with these roles
}

interface FirstTimeSetupChecklistProps {
  onDismiss?: () => void;
}

export function FirstTimeSetupChecklist({
  onDismiss,
}: FirstTimeSetupChecklistProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Check if user has admin/owner role for admin-only tasks
  const isAdmin = hasAnyRole(user, ['admin', 'owner']);

  // Fetch data to determine completion status with permissions
  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/users/list"],
    enabled: true, // Users list endpoint is available to all authenticated users
  });

  const { data: customers } = useQuery<any[]>({
    queryKey: ["/api/customers"],
    enabled: true, // Customers are core to the platform
  });

  const { data: assessments } = useQuery<any[]>({
    queryKey: ["/api/assessments"],
    enabled: true, // Assessments are core to the platform
  });

  const { data: qbrs } = useQuery<any[]>({
    queryKey: ["/api/qbrs"],
    enabled: true, // QBRs are core to the platform
  });

  const { data: organization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    enabled: true, // Current organization should be available to all authenticated users
  });

  const { data: integrations } = useQuery<any[]>({
    queryKey: ["/api/integrations/status"],
    enabled: isAdmin, // Only fetch for admins who can see integration status in checklist
  });

  const { data: techStackStandards } = useQuery<any[]>({
    queryKey: ["/api/tech-stack/standards"],
    enabled: true, // Tech stack standards are core to the platform
  });


  // Check completion status
  const hasMultipleUsers = Array.isArray(users) && users.length > 1;
  const hasCustomers = Array.isArray(customers) && customers.length > 0;
  const hasAssessments = Array.isArray(assessments) && assessments.length > 0;
  const hasPresentedQbr =
    Array.isArray(qbrs) &&
    qbrs.some(
      (qbr: any) =>
        qbr.status === "presented" ||
        qbr.status === "delivered" ||
        qbr.status === "summary_sent",
    );
  const hasSsoEnabled =
    !!(organization as any)?.settings?.sso?.isConfigured;
  const hasActiveIntegrations =
    Array.isArray(integrations) &&
    integrations.some((integration: any) => integration.status === "active");
  const hasTechStackStandards =
    Array.isArray(techStackStandards) && techStackStandards.length > 0;
  // Timezone is per-user setting - check user's timezone preference
  const hasSetTimezone = !!(user as any)?.timezone;

  const allSetupTasks: SetupTask[] = [
    {
      id: "set-timezone",
      title: "Set your timezone",
      description: "Configure your timezone for accurate date and time display",
      icon: <Clock className="h-5 w-5" />,
      href: "/profile?openTimezone=true",
      isCompleted: hasSetTimezone,
      // Available to all users - no requiredRoles
    },
    {
      id: "define-tech-stack",
      title: "Define Tech Stack Standards",
      description: "Set your organization's approved technology standards",
      icon: <Layers className="h-5 w-5" />,
      href: "/tech-stack-standards",
      isCompleted: hasTechStackStandards,
      requiredRoles: ["vcio", "admin", "owner"], // vCIO+ only
    },
    {
      id: "create-customer",
      title: "Create your first customer",
      description: "Add a customer to start conducting assessments",
      icon: <Building className="h-5 w-5" />,
      href: "/customers",
      isCompleted: hasCustomers,
      requiredRoles: ["vcio", "admin", "owner"], // vCIO+ can create customers
    },
    {
      id: "conduct-assessment",
      title: "Conduct an assessment",
      description: "Evaluate a customer's technology alignment",
      icon: <ClipboardCheck className="h-5 w-5" />,
      href: "/assessments",
      isCompleted: hasAssessments,
      // Available to all users - no requiredRoles
    },
    {
      id: "present-qbr",
      title: "Present a Business Review",
      description: "Deliver quarterly business review results to your customer",
      icon: <FileText className="h-5 w-5" />,
      href: "/qbrs",
      isCompleted: hasPresentedQbr,
      isOptional: true,
      requiredRoles: ["vcio", "admin", "owner"], // vCIO+ only
    },
    {
      id: "add-user",
      title: "Add a team member",
      description:
        "Invite colleagues to collaborate on assessments and Business Reviews",
      icon: <Users className="h-5 w-5" />,
      href: "/users",
      isCompleted: hasMultipleUsers,
      isOptional: true,
      requiredRoles: ["admin", "owner"], // Only admins can manage users
    },
    {
      id: "setup-sso",
      title: "Set up SSO",
      description: "Configure enterprise authentication for your team",
      icon: <Shield className="h-5 w-5" />,
      href: "/integrations#sso",
      isCompleted: hasSsoEnabled,
      isOptional: true,
      requiredRoles: ["admin", "owner"], // Only admins can configure SSO
    },
    {
      id: "add-integration",
      title: "Connect an integration",
      description: "Link with ConnectWise PSA, Microsoft Teams, or other tools",
      icon: <Plug className="h-5 w-5" />,
      href: "/integrations",
      isCompleted: hasActiveIntegrations,
      isOptional: true,
      requiredRoles: ["admin", "owner"], // Only admins can manage integrations
    },
  ];

  // Filter tasks based on user role - only show tasks the user has permission to access
  const setupTasks = allSetupTasks.filter((task) => {
    if (!task.requiredRoles) return true; // No role restriction, show to all
    return hasAnyRole(user, task.requiredRoles);
  });

  const completedTasks = setupTasks.filter((task) => task.isCompleted);
  const totalTasks = setupTasks.length;
  const progressPercentage = (completedTasks.length / totalTasks) * 100;

  // Calculate if all required tasks are completed
  const requiredTasksCompleted = setupTasks
    .filter((task) => !task.isOptional)
    .every((task) => task.isCompleted);

  // Check if checklist is dismissed in organization settings
  const isChecklistDismissed =
    organization?.settings?.checklist?.dismissed === true;

  // Dismiss checklist mutation
  const dismissChecklistMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest(
        "POST",
        "/api/organizations/checklist/dismiss",
        {},
      );
    },
    onSuccess: () => {
      // Invalidate organization data to reflect the change
      queryClient.invalidateQueries({
        queryKey: ["/api/organizations/current"],
      });
      toast({
        title: "Checklist dismissed",
        description:
          "You can always access setup features from the main navigation.",
      });
      onDismiss?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to dismiss checklist",
        variant: "destructive",
      });
    },
  });

  const handleDismiss = () => {
    dismissChecklistMutation.mutate();
  };

  const handleTaskClick = async (taskId: string) => {
    // Timezone is now per-user and set via profile page
    // No need to mark organization-level setting
  };

  // Don't show if dismissed
  if (isChecklistDismissed) {
    return null;
  }

  return (
    <TooltipProvider>
      <Card
        className="mb-4 border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 shadow-lg"
        data-testid="first-time-setup-checklist"
      >
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-primary">
                Welcome to TechAlign! Let's get you started
              </CardTitle>
              <p className="text-sm text-primary/70 mt-1">
                Complete these steps to unlock the full potential of the
                platform
              </p>
            </div>
            {requiredTasksCompleted ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="text-primary/60 hover:text-primary"
                data-testid="dismiss-checklist"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="text-primary/60 hover:text-primary"
                data-testid="collapse-checklist"
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>

          {!isCollapsed && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-primary/80">
                  Progress: {completedTasks.length} of {totalTasks} completed
                </span>
                <span className="text-primary/80 font-medium">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <Progress
                value={progressPercentage}
                className="h-3 bg-primary/10"
                data-testid="setup-progress"
              />
            </div>
          )}
        </CardHeader>

        {!isCollapsed && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {setupTasks.map((task) => (
                <Tooltip key={task.id}>
                  <TooltipTrigger asChild>
                    <Link
                      href={task.href}
                      onClick={() => handleTaskClick(task.id)}
                    >
                      <div
                        className={`
                        flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer
                        ${
                          task.isCompleted
                            ? "bg-success/10 border-success/30 hover:bg-success/15 shadow-sm"
                            : "bg-card border-primary/20 hover:bg-primary/5 hover:border-primary/30"
                        }
                      `}
                        data-testid={`setup-task-${task.id}`}
                      >
                        <div
                          className={`
                        flex-shrink-0 rounded-full p-1
                        ${
                          task.isCompleted
                            ? "setup-task-completed"
                            : "text-muted-foreground"
                        }
                      `}
                        >
                          {task.isCompleted ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <Circle className="h-5 w-5" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div
                              className={`
                            ${
                              task.isCompleted
                                ? "text-success"
                                : "text-primary/70"
                            }
                          `}
                            >
                              {task.icon}
                            </div>
                            <h3
                              className={`
                            font-medium text-sm truncate
                            ${
                              task.isCompleted
                                ? "text-success-foreground"
                                : "text-foreground"
                            }
                          `}
                            >
                              {task.title}
                              {task.isOptional && (
                                <Badge
                                  variant="secondary"
                                  className="ml-2 text-xs"
                                >
                                  Optional
                                </Badge>
                              )}
                            </h3>
                          </div>
                          <p
                            className={`
                          text-xs truncate mt-1
                          ${
                            task.isCompleted
                              ? "text-success/80"
                              : "text-muted-foreground"
                          }
                        `}
                          >
                            {task.description}
                          </p>
                        </div>

                        <ChevronRight
                          className={`
                        h-4 w-4 flex-shrink-0
                        ${
                          task.isCompleted
                            ? "text-success/60"
                            : "text-primary/50"
                        }
                      `}
                        />
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-medium">{task.title}</p>
                      <p className="text-sm">{task.description}</p>
                      {task.isOptional && (
                        <p className="text-xs text-muted-foreground">
                          This step is optional
                        </p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </TooltipProvider>
  );
}
